import clsx from "clsx";
import React, { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import DynamicFormField from "~/components/Forms/DealerPassportForm2/UI/DynamicFormField";
import { useTranslation } from "react-i18next";
import {
  getDealerOwnerLegal,
  getDealerOwnerIndividual,
} from "~/components/Forms/DealerPassportForm2/Owners/options";
import useGetOwners from "~/components/Forms/DealerPassportForm2/hook/useGetOwners";
import useUpdateOwners from "~/components/Forms/DealerPassportForm2/hook/useUpdateOwners";
import useDeleteOwners from "~/components/Forms/DealerPassportForm2/hook/useDeleteOwners";
import s from "./Owners.less";
import FileLoader from "~/components/FileLoader";
import IconInfo from "~/components/Icons/icon-arrow-circle";
import Button from "~/shared/ui/Fields/Button";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { validateInn } from "../requests/dadata";
import { useAddNotificationMutation } from "~/schemas/generated/graphql";
import { NOTIFICATIONS } from "~/constans/notifications";
import { usePermissions } from "~/shared/context/PermissionContext";

interface OwnersProps {
  DealerId: string;
  idArchive?: string;
  allDisabled: boolean;
}

const Owners = ({ DealerId, idArchive, allDisabled }: OwnersProps) => {
  const { hasPermission, loading: loadingPermissions } = usePermissions();
  const { t } = useTranslation();
  const dealerLegal = useMemo(() => getDealerOwnerLegal(t), [t]);
  const [percentage, setPercentage] = useState(true);
  const [party, setParty] = useState([]);
  const dealerIndividual = useMemo(() => getDealerOwnerIndividual(t), [t]);
  const {
    data: dataDealer,
    isFetching: isLoadingDealer,
    refetch,
  } = useGetOwners(DealerId);
  const { mutate: updateOwners, isLoading: isUpdating } = useUpdateOwners();
  const { mutate: deleteOwners, isLoading: isDeleteUpdating } =
    useDeleteOwners();
  const [addNotificationMutation] = useAddNotificationMutation({});

  const ownerSchema = yup.object().shape({
    id: yup.string(),
    ownerType: yup.string().oneOf(["legal", "individual"]),
    // legalForm: yup.string().nullable(),
    name: yup.string().nullable().required(t("validation.required")),
    inn: yup
      .string()
      .nullable()
      .when("ownerType", {
        is: (ownerType: string) => ownerType === "legal",
        then: (schema) =>
          schema
            .required(t("validation.invalid_inn"))
            .test("", "Некорректный инн", async (value, context) => {
              if (!value) {
                return true;
              }
              if (party.includes(value)) {
                return true;
              }
              try {
                const isValid = await validateInn(value);
                setParty((party) => (isValid ? [...party, value] : party));
                return isValid;
              } catch (e) {
                return false;
              }
            }),
        otherwise: (schema) => schema.notRequired().nullable(),
      }),
    ownershipPercentage: yup
      .string()
      .required("Обязательное поле")
      .test(
        "min-ownershipPercentage",
        "Доля должна быть больше 0",
        (value) => parseFloat(value || "0") > 0
      )
      .test(
        "max-ownershipPercentage",
        "Максимальная доля — 100",
        (value) => parseFloat(value || "0") <= 100
      ),
  });

  const formResolver = yup.object().shape({
    owners: yup
      .array()
      .default([])
      .of(ownerSchema)
      .required("Error")
      .test(
        "ownershipPercentageSum",
        "Сумма долей владения должна быть не более 100%",
        (owners) => {
          if (!owners || owners.length === 0) {
            return true;
          }
          const totalPercentage = owners.reduce((sum, owner) => {
            const percentage = parseFloat(owner.ownershipPercentage || "0");
            return sum + percentage;
          }, 0);
          const isValid = totalPercentage <= 100;
          setPercentage(isValid);
          return isValid;
        }
      ),
  });

  const method = useForm({
    defaultValues: { owners: [] },
    mode: "onChange",
    resolver: yupResolver(formResolver),
  });
  const {
    formState: { errors },
  } = method;

  useEffect(() => {
    if (dataDealer?.owners) {
      method.reset({ owners: dataDealer.owners });
      method.register("owners");
    }
  }, [dataDealer]);

  const addOwner = () => {
    const newOwner = {
      id: Date.now(),
      name: "",
      phone: "",
      mobilePhone: "",
      email: "",
      ownershipPercentage: "",
      legalForm: "ООО",
      inn: "",
      contactPerson: "",
      ownerType: "legal",
      isNew: true,
    };
    const currentOwners = method.getValues("owners") || [];

    const updatedOwners = [...currentOwners, newOwner];

    method.reset({ owners: updatedOwners });
    method.register("owners");
  };

  const changeOwnerType = (index) => {
    const newOwners = method.getValues("owners");
    const newOwnerType =
      newOwners[index].ownerType === "individual" ? "legal" : "individual";
    newOwners[index] = {
      ...newOwners[index],
      ownerType: newOwnerType,
      legalForm: newOwnerType === "legal" ? "ООО" : null,
      inn: "",
      contactPerson: "",
      scan: null,
      isNew: newOwners[index].isNew,
    };
    method.reset({ owners: newOwners });
    method.register("owners");
  };

  const removeOwner = (index) => {
    const owners = method.getValues("owners");
    const itemOwners = owners[index];
    const newOwners = owners.filter((item, i) => i !== index);
    if (itemOwners.isNew) {
      method.reset({ owners: newOwners });
      method.register("owners");
    } else {
      deleteOwners(
        { dealerId: DealerId, ownerId: itemOwners.id },
        {
          onSuccess: () => {
            method.reset({ owners: newOwners });
            method.register("owners");
          },
          onError: (error) => {
            console.error(`Error deleting owner ${itemOwners.id}:`, error);
            refetch();
          },
        }
      );
    }
  };

  const onSubmit = (data) => {
    const newData = data?.owners?.map((item) => {
      const { isNew, legalForm, ...rest } = item;
      const id = item.photoId
        ? Number(item.photoId)
        : item?.photo?.id
        ? item.photo.id
        : null;
      const scanId = item.scanId
        ? Number(item.scanId)
        : item?.scan?.id
        ? item.scan.id
        : null;
      return {
        ...rest,
        legalForm: typeof legalForm === "object" ? legalForm?.value : legalForm,
        id: Number(item.id),
        photoId: id,
        scanId,
      };
    });
    updateOwners(
      { dealerId: DealerId, data: newData },
      {
        onSuccess: () => {
          addNotificationMutation({
            variables: {
              type: NOTIFICATIONS.success,
              content: t("common.сhange_was_successful"),
            },
          });
          refetch();
        },
        onError: (error) => {
          console.error("Error updating owners:", error);
        },
      }
    );
  };

  const deleteFileByName = (
    index: number,
    fileField: string,
    idField: string
  ) => {
    const owners = method.getValues("owners");
    if (owners[index]) {
      owners[index][fileField] = null;
      owners[index][idField] = null;
      method.reset({ owners });
      method.register("owners");
    }
  };

  const notConsent = (scan, name) => {
    if (!scan) {
      switch (name) {
        case "phone":
        case "mobilePhone":
        case "email":
          return true;
        default:
          return false;
      }
    } else {
      return false;
    }
  };

  if (isLoadingDealer) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <FormProvider {...method}>
        <form
          onSubmit={method.handleSubmit(onSubmit)}
          className={clsx("dynamic-form")}
        >
          <div className={s.wrapper}>
            {method.getValues("owners") &&
              method.getValues("owners").map((item, index) => {
                return (
                  <div className={s.wrap} key={item.id}>
                    <div className={s.wrapperDealerImg}>
                      <FileLoader
                        name={`owners[${index}].photoId`}
                        withIMG
                        isNoneUpload={!!idArchive}
                        deleteFile={() =>
                          deleteFileByName(index, "photo", "photoId")
                        }
                        isNewStyle
                        download={item.photo}
                        control={method.control}
                        setValue={method.setValue}
                        disabled={allDisabled}
                      />
                    </div>

                    <div className={s.wrapperRight}>
                      <div className={s.wrapperBase}>
                        <div className={s.title}>
                          Собственник {index + 1}
                          <div className={s.wrapBase}>
                            {!allDisabled &&
                              hasPermission("dealer.deleteOwner") && (
                                <Button
                                  onClick={() => removeOwner(index)}
                                  small
                                  customClassName={s.butRemove}
                                  txtBtn="Убрать собственника"
                                />
                              )}
                            {item.isNew ? (
                              <div
                                className={s.click}
                                onClick={() => changeOwnerType(index)}
                              >
                                {item.ownerType === "legal"
                                  ? "юр лицо"
                                  : "физ лицо"}{" "}
                                <IconInfo />
                              </div>
                            ) : (
                              <div className={clsx(s.click, s.disabled)}>
                                {item.ownerType === "legal"
                                  ? "юр лицо"
                                  : "физ лицо"}{" "}
                              </div>
                            )}
                          </div>
                        </div>

                        {item.ownerType === "legal" ? (
                          <div>
                            {dealerLegal.map((field) => (
                              <DynamicFormField
                                key={`${item.id}-${field.name}`}
                                {...field}
                                name={`owners[${index}].${field.name}`}
                                defaultValue={
                                  typeof item[field.name] === "object"
                                    ? item[field.name]?.value || ""
                                    : item[field.name] || ""
                                }
                                ownerIndex={index}
                                ownerName={field.name}
                                ownerErrors={errors?.owners ?? null}
                                disabled={allDisabled}
                              />
                            ))}
                          </div>
                        ) : (
                          <div>
                            {dealerIndividual.map((field) => {
                              let meth = method.getValues("owners")[index];
                              if (field.name !== "ownershipScan") {
                                return (
                                  <DynamicFormField
                                    key={`${item.id}-${field.name}`}
                                    {...field}
                                    name={`owners[${index}].${field.name}`}
                                    defaultValue={item[field.name] || ""}
                                    ownerIndex={index}
                                    ownerName={field.name}
                                    ownerErrors={errors?.owners ?? null}
                                    disabled={
                                      notConsent(item.scan, field.name) ||
                                      allDisabled
                                    }
                                  />
                                );
                              }
                              return (
                                <FileLoader
                                  key={`${item.id}-${field.name}`}
                                  name={`owners[${index}].scanId`}
                                  withButton
                                  isNoneUpload={!!idArchive}
                                  deleteFile={() =>
                                    deleteFileByName(index, "scan", "scanId")
                                  }
                                  isNewStyleScan
                                  download={item.scan}
                                  control={method.control}
                                  setValue={method.setValue}
                                  label={t("ui.download_title_scan_owner")}
                                  titleDownload={t("ui.title_scan_owner")}
                                  disabled={allDisabled}
                                />
                              );
                            })}
                          </div>
                        )}
                        {!percentage && (
                          <div className={s.errorPercentage}>
                            <span>
                              Сумма долей собственников не должна быть более
                              100%
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            {!allDisabled && (
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Button
                  onClick={addOwner}
                  customClassName={s.butOwner}
                  txtBtn="Добавить собственника"
                />
              </div>
            )}
          </div>
          <div className={s.wrapperBTN}>
            {!allDisabled && (
              <Button
                txtBtn={"Сохранить изменения"}
                orange
                isForm
                customClassName={s.but}
                disabled={isUpdating || isDeleteUpdating}
              />
            )}
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default React.memo(Owners);
